/**
 * PublishValidationReport.jsx - Sprint 3
 * 
 * Componente visual para mostrar el reporte de validación del curso.
 * Muestra errores críticos, warnings y estadísticas de completitud.
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md - Sprint 3, Día 15
 */

import React, { useState } from 'react';
import { 
  CheckCircle2,
  AlertCircle,
  XCircle,
  Info,
  ChevronDown,
  ChevronRight,
  FileCheck,
  AlertTriangle
} from 'lucide-react';

const PublishValidationReport = ({ validationResult, onClose, onPublish }) => {
  const [expandedSections, setExpandedSections] = useState({
    errors: true,
    warnings: false,
    stats: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!validationResult) {
    return null;
  }

  const { valid, canPublish, errors, warnings, stats } = validationResult;

  // Agrupar errores por sección
  const errorsBySection = errors.reduce((acc, error) => {
    const section = error.section || 'General';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(error);
    return acc;
  }, {});

  // Agrupar warnings por sección
  const warningsBySection = warnings.reduce((acc, warning) => {
    const section = warning.section || 'General';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(warning);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className={`px-6 py-4 ${
        canPublish ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'
      }`}>
        <div className="flex items-start gap-3">
          {canPublish ? (
            <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h2 className={`text-lg font-semibold ${
              canPublish ? 'text-green-900' : 'text-red-900'
            }`}>
              {canPublish 
                ? '✓ Curso Listo para Publicar' 
                : '✗ Curso No Válido - Requiere Correcciones'
              }
            </h2>
            <p className={`text-sm mt-1 ${
              canPublish ? 'text-green-700' : 'text-red-700'
            }`}>
              {canPublish 
                ? 'Todas las validaciones pasaron correctamente. Puedes publicar el curso.'
                : `Se encontraron ${errors.length} error(es) crítico(s) que deben ser corregidos antes de publicar.`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Estadísticas Rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50 border-b">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{stats.totalWeeks}</div>
          <div className="text-sm text-gray-600">Semanas Totales</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{stats.completeWeeks}</div>
          <div className="text-sm text-gray-600">Semanas Completas</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{stats.totalErrors}</div>
          <div className="text-sm text-gray-600">Errores Críticos</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-amber-600">{stats.totalWarnings}</div>
          <div className="text-sm text-gray-600">Advertencias</div>
        </div>
      </div>

      {/* Barra de Progreso */}
      <div className="px-6 py-4 border-b">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Completitud del Curso
          </span>
          <span className="text-sm font-bold text-gray-900">
            {stats.completenessPercentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all ${
              stats.completenessPercentage === 100 
                ? 'bg-green-600' 
                : stats.completenessPercentage >= 50 
                  ? 'bg-blue-600' 
                  : 'bg-amber-600'
            }`}
            style={{ width: `${stats.completenessPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Contenido del Reporte */}
      <div className="max-h-96 overflow-y-auto">
        {/* Errores Críticos */}
        {errors.length > 0 && (
          <div className="border-b">
            <button
              onClick={() => toggleSection('errors')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-gray-900">
                  Errores Críticos ({errors.length})
                </span>
              </div>
              {expandedSections.errors ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSections.errors && (
              <div className="px-6 pb-4 space-y-4">
                {Object.entries(errorsBySection).map(([section, sectionErrors]) => (
                  <div key={section} className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                      <FileCheck className="w-4 h-4" />
                      {section}
                    </h4>
                    <ul className="space-y-2">
                      {sectionErrors.map((error, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 flex-shrink-0"></div>
                          <div className="flex-1">
                            <p className="text-red-800">
                              {error.block && <span className="font-semibold">[{error.block}] </span>}
                              {error.message}
                            </p>
                            {error.details && (
                              <pre className="mt-2 text-xs bg-white border border-red-200 rounded p-2 overflow-auto">
                                {JSON.stringify(error.details, null, 2)}
                              </pre>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Advertencias */}
        {warnings.length > 0 && (
          <div className="border-b">
            <button
              onClick={() => toggleSection('warnings')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-gray-900">
                  Advertencias ({warnings.length})
                </span>
              </div>
              {expandedSections.warnings ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSections.warnings && (
              <div className="px-6 pb-4 space-y-4">
                {Object.entries(warningsBySection).map(([section, sectionWarnings]) => (
                  <div key={section} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                      <FileCheck className="w-4 h-4" />
                      {section}
                    </h4>
                    <ul className="space-y-2">
                      {sectionWarnings.map((warning, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-1.5 flex-shrink-0"></div>
                          <div className="flex-1">
                            <p className="text-amber-800">
                              {warning.block && <span className="font-semibold">[{warning.block}] </span>}
                              {warning.message}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Estadísticas Detalladas */}
        <div>
          <button
            onClick={() => toggleSection('stats')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Info className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-900">
                Estadísticas Detalladas
              </span>
            </div>
            {expandedSections.stats ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.stats && (
            <div className="px-6 pb-4 space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Errores por Tipo</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Metadata:</span>
                    <span className="font-semibold text-gray-900">{stats.errorsByType.metadata}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Configuración:</span>
                    <span className="font-semibold text-gray-900">{stats.errorsByType.config}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contenido:</span>
                    <span className="font-semibold text-gray-900">{stats.errorsByType.content}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pesos:</span>
                    <span className="font-semibold text-gray-900">{stats.errorsByType.weights}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estructura:</span>
                    <span className="font-semibold text-gray-900">{stats.errorsByType.structure}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Advertencias por Bloque</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">B1 (Recursos):</span>
                    <span className="font-semibold text-gray-900">{stats.warningsByBlock.B1}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">B2 (Template):</span>
                    <span className="font-semibold text-gray-900">{stats.warningsByBlock.B2}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">B3 (Auditoría):</span>
                    <span className="font-semibold text-gray-900">{stats.warningsByBlock.B3}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">B4 (Coach IA):</span>
                    <span className="font-semibold text-gray-900">{stats.warningsByBlock.B4}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">B5 (Formalización):</span>
                    <span className="font-semibold text-gray-900">{stats.warningsByBlock.B5}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">B6 (Cross-Feedback):</span>
                    <span className="font-semibold text-gray-900">{stats.warningsByBlock.B6}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sin errores ni warnings */}
        {errors.length === 0 && warnings.length === 0 && (
          <div className="px-6 py-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              ¡Perfecto! No hay problemas detectados
            </h3>
            <p className="text-gray-600">
              El curso está completamente validado y listo para publicarse.
            </p>
          </div>
        )}
      </div>

      {/* Footer con Acciones */}
      <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
        >
          Cerrar Reporte
        </button>
        <div className="flex items-center gap-3">
          {!canPublish && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4" />
              <span>Corrige los errores para publicar</span>
            </div>
          )}
          <button
            onClick={onPublish}
            disabled={!canPublish}
            className={`px-6 py-2 rounded-lg font-semibold transition-all ${
              canPublish
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Publicar Curso
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishValidationReport;
